/**
 * Utility that can be used to log out map events
 * Useful for debugging and getting visibility into different
 * parts of the map lifecycle
 */
class MapLogger {
  constructor({ enabled = false, prefix }) {
    this.prefix = prefix;
    this.enabled = enabled;
  }

  log(event) {
    if (this.enabled) {
      console.log(`${this.prefix} - ${event}`);
    }
  }
}

/**
 * Translates the filter values into a Mapbox filter expression
 * Mapbox expressions are gnarly, powerful black box.
 * See https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/
 * The easiest way to get a feel for expressions is to create a new map
 * in Mapbox Studio, add a map layer, apply some filters to the layer and
 * then click on the code icon in the sidebar drawer to inspect the
 * Mapbox expression that is generated for the applied filters
 * @param {object} filterValues object representing all of the current
 * filter values
 */
export function buildFilterExpression(filterValues) {
  /**
   * Setting to all means that all conditions must be met
   * Equivalent to and in SQL, change to "any" for the or
   * equivalent
   */
  const mapFilterExpression = ["all"];
  Object.values(filterValues).forEach((filter) => {
    if (filter.type === "multi-select") {
      mapFilterExpression.push([
        "match",
        ["get", filter.layerFieldName],
        [...filter.value].length ? [...filter.value] : "",
        true,
        false,
      ]);
    } else if (filter.type === "multi-select-array") {
      const mapFilterExpressionTemp = ["case"];
      if (filter?.value?.length) {
        filter.value.forEach((item) =>
          mapFilterExpressionTemp.push(
            ["in", item, ["get", filter.layerFieldName]],
            true
          )
        );
      } else {
        mapFilterExpressionTemp.push(
          ["in", "", ["get", filter.layerFieldName]],
          true
        );
      }
      mapFilterExpressionTemp.push(false);
      mapFilterExpression.push(mapFilterExpressionTemp);
    } else if (filter.type === "boolean") {
      //MJB only apply filter if toggle is true
      //MJB no filter applied if toggle is false
      if (filter.value) {
        mapFilterExpression.push([
          "==",
          ["get", filter.layerFieldName],
          filter.value,
        ]);
      }
    } else if (filter.type === "simple-number") {
      mapFilterExpression.push([
        ">=",
        ["get", filter.layerFieldName],
        filter.value,
      ]);
    }
  });
  return mapFilterExpression;
}

export function getUniqueFeatures(features, comparatorProperty) {
  const uniqueIds = new Set();
  const uniqueFeatures = [];
  for (const feature of features) {
    const id = feature.properties[comparatorProperty];
    if (!uniqueIds.has(id)) {
      uniqueIds.add(id);
      uniqueFeatures.push(feature);
    }
  }
  return uniqueFeatures;
}

export const coordinatesGeocoder = function (query) {
  // Match anything which looks like
  // decimal degrees coordinate pair.
  const matches = query.match(
    /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i
  );
  if (!matches) {
    return null;
  }

  function coordinateFeature(lng, lat) {
    return {
      center: [lng, lat],
      geometry: {
        type: "Point",
        coordinates: [lng, lat],
      },
      place_name: "Lat: " + lat + " Lng: " + lng,
      place_type: ["coordinate"],
      properties: {},
      type: "Feature",
    };
  }

  const coord1 = Number(matches[1]);
  const coord2 = Number(matches[2]);
  const geocodes = [];

  if (coord1 >= -90 && coord1 <= 90 && coord2 >= -180 && coord2 <= 180) {
    // must be lat, lng
    geocodes.push(coordinateFeature(coord2, coord1));
  }

  if (coord2 >= -90 && coord2 <= 90 && coord1 >= -180 && coord1 <= 180) {
    // must be lng, lat
    geocodes.push(coordinateFeature(coord1, coord2));
  }

  return geocodes;
};

export { MapLogger };
